import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { HOSTPLAN } from "@/lib/testIds";
import { useHostPlans } from "@/hooks/useApiData";

const HOST_FAQ = [
  {
    q: "Do you take a fee on each booking?",
    a: "Yes. StayEasy retains a 12% platform fee from each completed booking. Stripe processing fees are paid by StayEasy, and the remaining 88% is routed to the host's connected Stripe account.",
  },
  {
    q: "What can I do on the Free plan?",
    a: "The Free plan lets you create one property as a draft. Publishing requires a paid host plan and completed Stripe payout setup.",
  },
  {
    q: "How do payouts work?",
    a: "StayEasy uses Stripe Connect destination charges. When a booking payment succeeds, Stripe routes the host share to the connected Stripe account as part of the payment flow. Bank payout timing then follows the host's Stripe payout schedule.",
  },
  {
    q: "Can I switch plans later?",
    a: "Yes. Starter allows up to 2 properties, Growth up to 4, and Pro is unlimited. If you move to a lower tier, existing properties are not deleted, but new property creation and publishing permissions follow the new plan limits.",
  },
];

export const HostSubscription = () => {
  const { plans } = useHostPlans();
  return (
    <main data-testid={HOSTPLAN.root} className="pb-24">
      <section className="mx-auto max-w-[1400px] px-5 pt-20 md:px-10 md:pt-32">
        <p className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground md:text-xs">
          For hosts · Nº 05
        </p>
        <h1 className="mt-5 font-display text-5xl font-medium leading-[0.95] tracking-tight md:mt-8 md:text-[9rem]">
          Simple plans,
          <br />
          <span className="italic">transparent</span> fees.
        </h1>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-foreground/80 md:mt-10 md:text-lg">
          Choose the property limit that fits your portfolio. On every completed
          booking, StayEasy retains a 12% platform fee. Stripe processing fees are
          paid by StayEasy, and the remaining 88% is routed to your connected Stripe account.
        </p>
      </section>

      <section className="mx-auto mt-16 max-w-[1400px] px-5 md:mt-24 md:px-10">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {plans.map((plan) => (
            <PlanCard key={plan.key || plan.id} plan={plan} />
          ))}
        </div>
        <p className="mt-8 text-center text-xs text-muted-foreground md:mt-10">
          Free hosts can create one draft property. Publishing is available on paid plans after Stripe payout setup is complete.
        </p>
      </section>

      <section className="mx-auto mt-24 max-w-[1200px] px-5 md:mt-32 md:px-10">
        <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground md:text-xs">
          Why it works
        </p>
        <h2 className="mt-4 font-display text-4xl font-medium leading-[0.95] tracking-tight md:text-6xl">
          Predictable pricing.
          <br />
          <span className="italic">Clear</span> earnings.
        </h2>
        <div className="mt-12 grid grid-cols-1 gap-x-12 gap-y-10 md:grid-cols-3">
          {[
            {
              title: "12% platform fee",
              body:
                "StayEasy retains 12% of each booking. Stripe processing fees are paid by StayEasy, so the host share is 88% of the booking amount.",
            },
            {
              title: "Stripe Connect payouts",
              body:
                "Stripe routes your booking share to your connected account as part of the payment flow. Bank payout timing follows your Stripe account schedule.",
            },
            {
              title: "Plans that scale",
              body:
                "Start with one draft property for free, then move to 2, 4 or unlimited properties as your portfolio grows.",
            },
          ].map((b, i) => (
            <div key={b.title}>
              <p className="text-[10px] uppercase tracking-[0.2em] text-primary md:text-xs">
                0{i + 1}
              </p>
              <h3 className="mt-3 font-display text-xl font-medium tracking-tight md:text-2xl">
                {b.title}
              </h3>
              <p className="mt-3 text-base leading-relaxed text-muted-foreground">
                {b.body}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto mt-24 max-w-[900px] px-5 md:mt-32 md:px-10">
        <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground md:text-xs">
          Host questions
        </p>
        <h2 className="mt-4 font-display text-4xl font-medium leading-[0.95] tracking-tight md:text-5xl">
          What hosts ask us
          <br />
          <span className="italic">before</span> signing up.
        </h2>
        <div className="mt-10">
          <Accordion type="single" collapsible>
            {HOST_FAQ.map((f, i) => (
              <AccordionItem
                key={f.q}
                value={`hf-${i}`}
                className="border-foreground/10"
              >
                <AccordionTrigger className="py-6 font-display text-lg font-medium tracking-tight hover:no-underline md:text-xl">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-base leading-relaxed text-muted-foreground">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="mx-auto mt-24 max-w-[1400px] px-5 md:mt-32 md:px-10">
        <div className="relative overflow-hidden bg-foreground p-10 text-background md:p-20">
          <p className="text-[10px] uppercase tracking-[0.28em] text-background/60 md:text-xs">
            Ready when you are
          </p>
          <h3 className="mt-5 font-display text-4xl font-medium leading-[0.95] tracking-tight md:mt-8 md:text-7xl">
            Create your first home
            <br />
            <span className="italic">today</span>.
          </h3>
          <Link
            to="/hosts/register?plan=free"
            className="mt-10 inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-medium text-primary-foreground transition-transform duration-300 hover:-translate-y-0.5 md:mt-14"
          >
            Start free <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </main>
  );
};

const PlanCard = ({ plan }) => {
  const isFeatured = plan.featured;
  const planKey = plan.key || plan.id;
  return (
    <article
      data-testid={HOSTPLAN.plan(planKey)}
      className={`relative flex flex-col border p-8 transition-colors duration-300 ${
        isFeatured
          ? "border-primary bg-[#2E4034] text-[#F6F4EF]"
          : "border-foreground/15 bg-card text-foreground hover:border-foreground/40"
      }`}
    >
      {isFeatured && (
        <span className="absolute -top-3 left-8 rounded-full bg-primary px-3 py-1 text-[10px] font-medium uppercase tracking-[0.18em] text-primary-foreground">
          Recommended
        </span>
      )}
      <p
        className={`text-[10px] uppercase tracking-[0.2em] ${
          isFeatured ? "text-[#F6F4EF]/70" : "text-muted-foreground"
        }`}
      >
        {plan.name}
      </p>
      <p className="mt-6">
        <span className="font-display text-5xl font-medium tracking-tight">
          ${plan.price}
        </span>
        <span
          className={`ml-1 text-sm ${
            isFeatured ? "text-[#F6F4EF]/70" : "text-muted-foreground"
          }`}
        >
          / mo
        </span>
      </p>
      <p
        className={`mt-3 text-sm leading-relaxed ${
          isFeatured ? "text-[#F6F4EF]/80" : "text-muted-foreground"
        }`}
      >
        {plan.tagline}
      </p>

      <ul className="mt-8 flex-1 space-y-4">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-3 text-sm">
            <Check
              size={14}
              className={`mt-0.5 shrink-0 ${
                isFeatured ? "text-primary" : "text-primary"
              }`}
            />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <Link
        to={`/hosts/register?plan=${planKey}`}
        data-testid={HOSTPLAN.cta(planKey)}
        className={`mt-10 inline-flex items-center justify-center gap-2 rounded-full py-3 text-sm font-medium transition-transform duration-300 hover:-translate-y-0.5 ${
          isFeatured
            ? "bg-primary text-primary-foreground"
            : "bg-foreground text-background"
        }`}
      >
        {plan.cta} <ArrowRight size={14} />
      </Link>
    </article>
  );
};

export default HostSubscription;
